import React, { useEffect, useState, useRef } from "react";

// CSS
import "./dropDown.css";

// Components
import SearchBar from "./searchBar";
import SvgArrowIcon from "../../SvgIcons/ArrowIcon";

import { useTranslation } from "react-i18next";

const MIN_OPTIONS_FOR_SEARCH_BAR = 4;

export default function DropDown({
  options,
  value,
  onChange,
  sortType = "string",
}) {
  const [collapsed, setCollapsed] = useState(true);
  const [selectedOption, setSelectedOption] = useState(value);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortedOptions, setSortedOptions] = useState(options || []);

  const [filteredOptions, setFilteredOptions] = useState(sortedOptions || []);

  const dropDownRef = useRef(null);

  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setCollapsed(true);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!options || options.length === 0) return;
    let objType = false;
    if (typeof options[0] === "object") objType = true;
    const newSelected =
      value === undefined || value === ""
        ? options[0]
        : options.find((opt) =>
            objType ? opt.value === value : opt === value
          );
    setSelectedOption(newSelected);
    if (objType ? newSelected?.value !== value : newSelected !== value) {
      onChange(newSelected?.value ?? newSelected);
    }
    let newSortedOptions;
    if (sortType === "string")
      newSortedOptions = [...options].sort((a, b) => {
        return (a.value ?? a).toString().toLowerCase() >=
          (b.value ?? b).toString().toLowerCase()
          ? 1
          : -1;
      });
    else if (sortType === "number")
      newSortedOptions = [...options].sort((a, b) => {
        return (a.value ?? a) - (b.value ?? b);
      });
    else newSortedOptions = [...options];
    setSortedOptions(newSortedOptions);
  }, [value, options, onChange]);

  useEffect(() => {
    if (!sortedOptions) return;

    if (searchTerm === "") {
      setFilteredOptions(sortedOptions);
    } else {
      const filtered = sortedOptions.filter((option) => {
        const label =
          typeof option === "object" ? option.label || option.value : option;
        return String(label).toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, sortedOptions]);

  const createOptions = (item, index) => {
    const label = typeof item === "object" ? item.label ?? item.value : item;
    return (
      <li
        key={index}
        className="drop-down item"
        onClick={() => onOptionClick(item, index)}
      >
        {label}
      </li>
    );
  };

  const onSelectedOptionClick = () => {
    setCollapsed(!collapsed);
    if (!collapsed) {
      setSearchTerm("");
    }
  };

  const onOptionClick = (option, index) => {
    setCollapsed(true);
    setSelectedOption(option);
    setSearchTerm("");
    onChange(option.value ?? option);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  return (
    <div className="drop-down" ref={dropDownRef}>
      <div
        className={"drop-down-content " + (collapsed ? "" : "extended")}
        onClick={onSelectedOptionClick}
      >
        <div className="drop-down selected-option">
          {typeof selectedOption === "object"
            ? selectedOption.label ?? selectedOption.value
            : selectedOption}
        </div>
        <div className={"drop-down arrow"}>
          <SvgArrowIcon className={collapsed ? "down" : "up"} />
        </div>
      </div>
      <div
        className={
          collapsed
            ? "drop-down list-container collapsed"
            : "drop-down list-container"
        }
      >
        {options && options.length > MIN_OPTIONS_FOR_SEARCH_BAR && (
          <div className="drop-down search-container">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder="Search..."
              className="drop-down-search-bar"
            />
          </div>
        )}
        <ul className="drop-down list">
          {filteredOptions.length > 0 ? (
            filteredOptions.map(createOptions)
          ) : (
            <li className="drop-down no-results">{t("No results found")}</li>
          )}
        </ul>
      </div>
    </div>
  );
}
